import React from "react";
import { AlertCircle, X, Power, PowerOff, CheckCircle, XCircle } from "lucide-react";

/**
 * Status Confirmation Modal Component
 * Reusable modal for confirming status changes (activate/deactivate)
 */
const StatusConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  organizer,
  isLoading = false,
}) => {
  if (!isOpen || !organizer) return null;

  const isActivating = organizer.status === "inactive";
  const newStatus = isActivating ? "active" : "inactive";
  const iconBgColor = isActivating ? "bg-green-100" : "bg-orange-100";
  const iconColor = isActivating ? "text-green-600" : "text-orange-600";
  const buttonColor = isActivating
    ? "bg-green-600 hover:bg-green-700"
    : "bg-orange-600 hover:bg-orange-700";
  const warningBgColor = isActivating ? "bg-green-50" : "bg-orange-50";
  const warningBorderColor = isActivating ? "border-green-100" : "border-orange-100";
  const warningTextColor = isActivating ? "text-green-800" : "text-orange-800";
  const warningIconColor = isActivating ? "text-green-600" : "text-orange-600";

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={isLoading ? undefined : onClose}
      />
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl duration-200 animate-in fade-in-0 zoom-in-95">
        {/* Header */}
        <div className="flex items-start mb-6 space-x-4">
          <div className={`flex-shrink-0 p-3 ${iconBgColor} rounded-full`}>
            {isActivating ? (
              <Power className={`w-6 h-6 ${iconColor}`} />
            ) : (
              <PowerOff className={`w-6 h-6 ${iconColor}`} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="mb-1 text-lg font-bold text-gray-900">
              {isActivating ? "Activate Organizer" : "Deactivate Organizer"}
            </h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to{" "}
              <span className="font-semibold text-gray-900">
                {isActivating ? "activate" : "deactivate"}
              </span>{" "}
              the organizer{" "}
              <span className="font-semibold text-gray-900">{organizer.name}</span>?
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-shrink-0 p-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Status Display */}
        <div className="p-4 mb-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Current Status:</span>
            {organizer.status === "active" ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                <CheckCircle className="mr-1 w-3 h-3" />
                Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200">
                <XCircle className="mr-1 w-3 h-3" />
                Inactive
              </span>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm font-medium text-gray-700">New Status:</span>
            {newStatus === "active" ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                <CheckCircle className="mr-1 w-3 h-3" />
                Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200">
                <XCircle className="mr-1 w-3 h-3" />
                Inactive
              </span>
            )}
          </div>
        </div>

        {/* Warning Message */}
        <div className={`p-4 mb-6 ${warningBgColor} rounded-xl border ${warningBorderColor}`}>
          <div className="flex items-start space-x-3">
            <AlertCircle className={`w-5 h-5 ${warningIconColor} flex-shrink-0 mt-0.5`} />
            <div className={`text-sm ${warningTextColor}`}>
              <p className="mb-1 font-medium">
                {isActivating ? "Activation Notice:" : "Deactivation Notice:"}
              </p>
              <p>
                {isActivating
                  ? "This organizer will be able to create and manage events once activated."
                  : "This organizer will not be able to create or manage events while deactivated."}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`flex flex-1 justify-center items-center px-4 py-3 font-semibold text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${buttonColor}`}
          >
            {isLoading ? (
              <>
                <div className="mr-2 w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                {isActivating ? "Activating..." : "Deactivating..."}
              </>
            ) : (
              <>
                {isActivating ? (
                  <>
                    <Power className="mr-2 w-4 h-4" />
                    Activate
                  </>
                ) : (
                  <>
                    <PowerOff className="mr-2 w-4 h-4" />
                    Deactivate
                  </>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusConfirmationModal;

